import { AlertTriangle, Check, Loader2, Plus } from 'lucide-react';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { cn } from '@/lib/utils';
import { coverageApiClient } from '@/services/api/coverageService';
import { miniTortApiClient } from '@/services/api/miniTortService';
import { CoverageCategory, IdentifierType } from '@/services/types/coverage-types';

import type { MiniTortClaimDetail, MiniTortClaimFlag } from './types';
import { PrerequisiteStatus } from './types';
import { extractApiError } from './utils';

interface MiniTortManualCoverageProps {
  claim: MiniTortClaimDetail;
  flags: MiniTortClaimFlag[];
  className?: string;
}

type CoverageType = '1p' | '3p';

/** Show manual coverage entry forms when extraction failed and coverage is pending */
export const MiniTortManualCoverage: React.FC<MiniTortManualCoverageProps> = ({
  claim,
  flags,
  className,
}) => {
  const needs1p =
    claim.coverage_1p_status === PrerequisiteStatus.PENDING ||
    claim.coverage_1p_status === PrerequisiteStatus.FAILED;
  const needs3p =
    claim.coverage_3p_status === PrerequisiteStatus.PENDING ||
    claim.coverage_3p_status === PrerequisiteStatus.FAILED;

  // Only show if there's a related blocker flag for extraction failure
  const hasExtractionBlocker = flags.some(
    (f) =>
      !f.resolved &&
      f.severity === 'blocker' &&
      (f.flag_type.includes('extraction') || f.flag_type.includes('coverage')),
  );

  if (!hasExtractionBlocker && !needs1p && !needs3p) return null;
  if (claim.coverage_1p_status === PrerequisiteStatus.EXTRACTED && claim.coverage_3p_status === PrerequisiteStatus.EXTRACTED) return null;

  return (
    <div className={cn('rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/10', className)}>
      <div className="mb-3 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-200">
          Manual Coverage Entry Required
        </h3>
      </div>
      <p className="mb-4 text-xs text-amber-700 dark:text-amber-300">
        Automated coverage extraction failed or is incomplete. Enter coverage details manually so the claim can advance.
      </p>

      <div className="space-y-4">
        {needs1p && (
          <CoverageEntryForm
            coverageType="1p"
            label="1P Coverage (Claimant's Insurance)"
            matterId={claim.matter_id}
            claimId={claim.id}
            status={claim.coverage_1p_status}
          />
        )}
        {needs3p && (
          <CoverageEntryForm
            coverageType="3p"
            label="3P Coverage (At-Fault Party's Insurance)"
            matterId={claim.matter_id}
            claimId={claim.id}
            status={claim.coverage_3p_status}
          />
        )}
      </div>
    </div>
  );
};

const CoverageEntryForm: React.FC<{
  coverageType: CoverageType;
  label: string;
  matterId: string;
  claimId: string;
  status: PrerequisiteStatus;
}> = ({ coverageType, label, matterId, claimId, status }) => {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const [carrierName, setCarrierName] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [createCoverage, { isLoading }] = coverageApiClient.useCreateCoverageMutation();

  const handleSubmit = async () => {
    if (!carrierName.trim()) {
      setError('Carrier name is required');
      return;
    }
    setError(null);

    try {
      await createCoverage({
        matterId,
        coverage: {
          carrier: { name: carrierName.trim(), handles: null, address: null },
          policy_identifiers: policyNumber.trim()
            ? [{ identifier_type: IdentifierType.POLICY_NUMBER, identifier_value: policyNumber.trim() }]
            : null,
          category: coverageType === '1p' ? CoverageCategory.FIRST_PARTY : CoverageCategory.THIRD_PARTY,
        },
      }).unwrap();

      // Invalidate mini-tort caches so prerequisites update
      dispatch(miniTortApiClient.util.invalidateTags([
        { type: 'MiniTortClaim', id: claimId },
        { type: 'MiniTortStatus', id: claimId },
        { type: 'MiniTortFlags', id: claimId },
        'MiniTortClaims',
      ]));

      setSuccess(true);
    } catch (err) {
      setError(extractApiError(err, 'Failed to create coverage'));
    }
  };

  if (success) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 dark:border-emerald-800 dark:bg-emerald-900/20">
        <Check className="h-4 w-4 text-emerald-600" />
        <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
          {label} created successfully. Prerequisites will update shortly.
        </span>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-zinc-800 dark:text-zinc-200">{label}</p>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            Status: <span className="font-medium text-red-600 dark:text-red-400">{status}</span>
          </p>
        </div>
        {!expanded && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
          >
            <Plus className="h-3.5 w-3.5" />
            Enter Manually
          </button>
        )}
      </div>

      {expanded && (
        <div className="mt-3 space-y-3 border-t border-zinc-100 pt-3 dark:border-zinc-800">
          <div>
            <p className="mb-1 text-xs font-medium text-zinc-700 dark:text-zinc-300">
              Carrier Name <span className="text-red-500">*</span>
            </p>
            <input
              type="text"
              aria-label="Carrier name"
              value={carrierName}
              onChange={(e) => { setCarrierName(e.target.value); setError(null); }}
              placeholder="e.g., State Farm, Progressive..."
              className="h-8 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-700 placeholder:text-zinc-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
            />
          </div>
          <div>
            <p className="mb-1 text-xs font-medium text-zinc-700 dark:text-zinc-300">
              Policy Number
            </p>
            <input
              type="text"
              aria-label="Policy number"
              value={policyNumber}
              onChange={(e) => setPolicyNumber(e.target.value)}
              placeholder="e.g., POL-12345"
              className="h-8 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-700 placeholder:text-zinc-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
            />
          </div>
          {error && <p className="text-xs font-medium text-red-600">{error}</p>}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading || !carrierName.trim()}
              className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Create Coverage
            </button>
            <button
              type="button"
              onClick={() => { setExpanded(false); setError(null); }}
              className="rounded-md px-3 py-1.5 text-xs font-medium text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
